import { Formik, Form } from 'formik'
import moment from 'moment'
import { memo, useEffect, useState, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import * as Yup from 'yup'
import { omit, isEmpty } from 'ramda'
import {
  useDialogController,
  useToast,
  TextLink,
  Notice,
  Text,
  TooltipRenderer,
  Box
} from '@machobear/design-system'
import { Button } from '@/components/button'
import {
  Label,
  Row,
  Group,
  FieldInput,
  FieldDatePicker,
  FieldSwitchToggleSmall
} from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import ContentWrapper from '@/components/content-wrapper'
import Panel from '@/components/panel'
import config from '@/config'
import { DefaultButtonBar } from '@/components/button-bar'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import { isValidSIN } from '@/utils/is-valid-sin'
import { colors, fontWeights, fontSizes, breakpoints } from '@/styles'
import Tooltip from '@/components/custom-tooltip'
import {
  CreditCheckToolTipIcon,
  ChevronIcon,
  CreditCheckChangeAddressIcon
} from '@/components/icons'
import { toast } from '@/components/toast-notifications'
import showProgressLostModal from './limit-reached-modal'
import AddressCompleteLogo from './address_complete_logo_en.png'
import showConfirmAddressSelectionModal from './confirm-address-selection-modal'
import CreditCheckModal from './credit-check-modal'

const { WEB_URL } = config

const Subtitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.39px;
  line-height: 22px;
  margin-top: ${props => props.top || 0}px;
  margin-bottom: 20px;
`

const PanelContainer = styled(Panel)`
  margin: 60px auto 30px;
  > :last-child {
    padding-bottom: 0px;
    border-bottom-width: 0px;
  }
`

const LinkText = styled.a`
  color: ${colors.link};
`

const AddressChangeLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const AddressChangeButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.link};
  cursor: pointer;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: start;
  }
`

const AddressChangeLabel = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${colors.grey24};
`

const AddressChangeCounter = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
`

const TooltipWrapper = styled.div`
  cursor: pointer;
  display: inherit;
`

const AddressResultsDropdown = styled.div`
  position: absolute;
  width: calc(100% - 20px);
  border: 1.5px solid ${colors.grey88};
  background-color: #fafafa;
  min-height: 152px;
  z-index: 1;
`

const LogoContainer = styled.div`
  position: absolute;
  bottom: 0;
  border-top: 1.5px solid ${colors.grey88};
  background-color: #fafafa;
  width: 100%;
  height: 33px;
  display: flex;
  align-items: center;
  padding: 0 10px;
`

const TextContainer = styled.div`
  position: relative;
  overflow: hidden;
  padding: 5px;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
`

const AddressResultsContainer = styled.div`
  overflow-y: scroll;
  height: 119px;

  span {
    font-size: 14px;
    font-weight: 400;
    b {
      font-weight: 700;
    }
  }
  svg {
    position: absolute;
    right: 5px;
    margin-top: 5px;
    transform: rotate(270deg);
  }
`

const UseThisAddressOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 52px;
`

const UseThisAddressOptionTextWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${colors.grey56};

  svg {
    margin-left: 5px;
  }
`

const ContentWrapperStyled = styled(ContentWrapper)`
  input:not(:placeholder-shown) + label {
    color: ${colors.foregroundTertiary};
  }

  input[disabled] {
    opacity: 1;
    background-color: ${colors.grey96};
    color: ${colors.grey56};
  }
`

const FieldSwitchToggleSmallWrapper = styled.div`
  display: flex;
`

const FieldSwitchToggleSmallStyled = styled(FieldSwitchToggleSmall)`
  &.toggled {
    pointer-events: none;
    &:before {
      opacity: 0.5;
    }
  }
`

const AddRessSearchInputWrapper = styled.div`
  .MmILz {
    color: #fe5348;
    font-size: 12px;
    line-height: 16px;
    margin: 0;
    width: 100%;
    text-align: left;
    margin-top: 0px;
    margin-right: 0px;
    margin-bottom: 0px;
    margin-left: 0px;
  }
`

const AddressSearchInput = styled(FieldInput)`
  &.error {
    border-color: #fe5348 !important;
  }
`

const CreditCheck = ({ history, location }) => {
  const profile = useProfile()
  const verified =
    profile?.liv_score?.credit_rating !== 'not enough' &&
    profile?.liv_score?.credit_rating !== undefined
  useEffect(() => {
    if (verified) {
      history.goBack()
    }
  }, [history, verified])
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const showToast = useToast()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const colSize = displayMobile ? 12 : 6
  const colSizeFull = 12
  const [currentAddress, setCurrentAddress] = useState(profile?.company_info?.address)
  const [currentValues, setCurrentValues] = useState([])
  const [availableAttempts, setAvailableAttempts] = useState(3)
  const isVerifiedAddressId = profile?.company_info?.address_lookup_id
  const [isAddressEmpty, setIsAddressEmpty] = useState(isVerifiedAddressId == null)
  const [lookupInputError, setLookupInputError] = useState(null)
  const [toggled, setToggled] = useState(false)
  const ref = useRef()
  const minCounterValue = 0
  const [actionType, setActionType] = useState(null)
  const { visible, showModal, closeModal } = useDialogController(false)

  useEffect(() => {
    if (isVerifiedAddressId && isAddressEmpty) {
      setIsAddressEmpty(false)
    }
  }, [isVerifiedAddressId])
  useEffect(() => {
    const asyncFunction = async () => {
      const result = await dispatch.profile.retrieveLookupCount()
      setAvailableAttempts(result?.body?.remaining)
    }

    asyncFunction()
  }, [currentAddress, dispatch.profile])

  useEffect(() => {
    if (isVerifiedAddressId == null) return
    const address = profile?.company_info?.address.split(/ (.*)/s) || []

    setCurrentAddress({
      id: availableAttempts,
      unit_number: profile?.company_info?.unit_number,
      street_number: address[0],
      street_name: address[1],
      neighbourhood: null,
      state: profile?.company_info?.state,
      state_code: profile?.company_info?.state,
      city: profile?.company_info?.city,
      zip: profile?.company_info?.zip,
      country: 'Canada',
      country_code: profile?.company_info?.country_code,
      origin: 'BE',
      external_key: null,
      metadata: []
    })
  }, [isVerifiedAddressId])
  useEffect(() => {
    if (!currentAddress) {
      return
    }
    const formValues = ref?.current?.values ?? {}
    ref?.current?.setValues({
      ...formValues,
      suite: currentAddress?.unit_number,
      street_name: currentAddress?.street_name,
      street_number: currentAddress?.street_number,
      city: currentAddress?.city,
      province: currentAddress?.state_code,
      zip: currentAddress?.zip
    })
  }, [currentAddress, currentValues])
  const handleModalClose = () => {
    closeModal()
    setActionType(null)
  }

  const handleActionTypeHandler = type => {
    const errorCheckList = omit(
      ['city', 'province', 'street_number', 'street_name'],
      ref?.current?.errors
    )

    if (!isEmpty(errorCheckList) && type === 'verify') {
      return ref.current?.submitForm()
    } else {
      setActionType(type)
      type !== null && showModal()
    }
  }

  const submitCreditCheck = async () => {
    try {
      await ref.current?.submitForm()
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleBackPropFunction = () => {
    const state = location?.state || {}
    const { onSuccessRedirectTo, onBackRedirectTo, ...restState } = state
    history.replace({
      pathname: onBackRedirectTo || '/profile/trust-score',
      state: restState
    })
  }

  if (!profile.id) return null

  const address = profile?.company_info?.address.split(/ (.*)/s) || []

  const initialValues = {
    first_name: profile.first_name,
    last_name: profile.last_name,
    dob: profile?.dob || '',
    sin: '',
    driver_license: '',
    suite: profile?.company_info?.unit_number || '',
    street_name: address[1] || '',
    street_number: address[0] || '',
    city: profile?.company_info?.city || '',
    province: profile?.company_info?.state || '',
    zip: profile?.company_info?.zip || '',
    country: 'ca',
    country_code: profile?.company_info?.country_code,
    terms: true,
    privacy: false,
    canada_post: null
  }

  const validate = values => {
    let errors = {}
    if (!isValidSIN(values.sin) && values.sin?.length > 0) {
      errors.sin = t(
        'b.profile.name.please_enter_valid_sin.error',
        'Please enter a valid SIN number.'
      )
    }
    if (!/^(?!.*[DFIOQU])[A-Za-z][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]$/.test(values.zip)) {
      errors.zip = t('b.profile.name.zip_error.error', 'Incorrect format (A1A 1A1)')
    }
    if (isAddressEmpty) {
      errors.canada_post = t('b.profile.name.required.error', 'Required')
    }
    return errors
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(
        /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .required(),
    last_name: Yup.string()
      .matches(
        /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .required(),
    dob: Yup.string().required(),
    driver_license: Yup.string(),
    sin: Yup.string(),
    suite: Yup.string(),
    street_name: Yup.string().required(),
    street_number: Yup.string().required(),
    city: Yup.string().required(),
    province: Yup.string().required(),
    zip: Yup.string().required(),
    country: Yup.string().required(),
    privacy: Yup.bool().required(),
    terms: Yup.bool().required()
  })

  const initialMonth = () => {
    return profile.dob ? moment(profile.dob) : moment().subtract(18, 'years')
  }

  const handleAdditionlSearch = async id => {
    const results = await dispatch.profile.findMoreAddress(id)
    setCurrentValues(results.body)
  }

  const handleRetrieve = async id => {
    const address = currentValues.find(x => x.Id === id).Text
    showConfirmAddressSelectionModal({ id, address, availableAttempts, handleRetrieveAddress })
  }

  const handleRetrieveAddress = async id => {
    const results = await dispatch.profile.retrieveAddress(id)
    setCurrentAddress(results.body.address)
    setCurrentValues([])
    setIsAddressEmpty(false)

    if (results?.response?.ok) {
      const count = await dispatch.profile.retrieveLookupCount()
      toast(
        t('r.credit.score.address_check.toast.text', {
          defaultValue:
            'Success! Your address has been changed. You now have {{count}} address changes remaining',
          count: count?.body?.remaining
        }),
        {
          title: t('r.credit.score.address_check.toast.title', 'Success'),
          type: 'success'
        }
      )
    }
  }

  const handleLimitModal = () => {
    if (availableAttempts <= minCounterValue) {
      showProgressLostModal()
      return
    }
  }

  const handleChange = async event => {
    handleLimitModal()

    if (event?.target?.value === '' || availableAttempts <= minCounterValue) return
    const results = await dispatch.profile.findAddress(event.target.value)
    if (results?.body?.[0]?.Error) {
      showToast({
        tone: 'critical',
        message: t('r.credit.score.address_check_error.toast.title', "Error: Can't verify address"),
        description: (
          <Trans i18nKey="r.credit.score.address_check_error.toast.text">
            We're currently unable to verify this address. Please contact{' '}
            <TextLink href="mailto:support@liv.rent">support@liv.rent</TextLink> for assistance.
          </Trans>
        )
      })
      return
    }

    if (results.body.length === 0) {
      setLookupInputError(true)
      return
    }
    setLookupInputError(false)
    ref.current.setTouched({ canada_post: true }, false)

    setCurrentValues(results.body)
  }

  const handleChangeAddress = () => {
    handleLimitModal()
    setCurrentAddress(undefined)
    setIsAddressEmpty(true)
    setToggled(false)
  }

  const handleToogle = e => {
    if (e) {
      dispatch.profile.updateMyAddress(currentAddress.id)
    }
    setToggled(true)
  }

  const formatText = item => {
    const isNext = item.Next === 'Find'
    const stylings = []
    const highlight = item.Highlight.split(',')
    highlight.map(highlightItem => {
      const indexes = highlightItem.split('-')
      stylings.push({
        start: parseInt(indexes[0]),
        end: parseInt(indexes[1]),
        type: 'b'
      })
    })
    const result = [...item.Text].reduce((a, v, i) => {
      stylings.filter(s => s.start === i).forEach(s => (a += `<${s.type}>`))
      stylings.filter(s => s.end === i).forEach(s => (a += `</${s.type}>`))
      return a + v
    }, '')
    const additionalSearch = isNext ? ' - ' + item.Description : ''

    return (
      <TextContainer
        onClick={isNext ? () => handleAdditionlSearch(item.Id) : () => handleRetrieve(item.Id)}
        key={item?.Id}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: result + additionalSearch
          }}
        />
        {item.Next === 'Find' && <ChevronIcon />}
      </TextContainer>
    )
  }

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validate={validate}
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          const result = await dispatch.livScore.submitCreditReportData(values)
          if (!result.response.ok) return

          setSubmitting(false)

          const state = location?.state || {}
          const { onSuccessRedirectTo, ...restState } = state
          history.push({
            pathname: onSuccessRedirectTo || '/profile/trust-score',
            state: restState
          })
        }}
        innerRef={ref}
        enableReinitialize
      >
        {({ values, isSubmitting }) => (
          <Form className="flex-full" autoComplete="off">
            <Page>
              <Helmet>
                <title>{t('r.profile.edit.credit_check.title', 'Credit Check')}</title>
              </Helmet>
              <PageTitle>{t('r.profile.edit.credit_check.title', 'Credit Check')}</PageTitle>
              <ContentWrapperStyled>
                <PanelContainer hSpace={30} vSpace={40}>
                  <Subtitle>
                    {t('r.profile.edit.credit_check_your_details.label', 'Your Details')}
                  </Subtitle>
                  <Row>
                    <Group col={colSize}>
                      <FieldInput
                        name="first_name"
                        placeholder={t(
                          'r.profile.edit.credit_check_first_name.label',
                          'Legal first name'
                        )}
                        required
                      />
                    </Group>
                    <Group col={colSize}>
                      <FieldInput
                        name="last_name"
                        placeholder={t(
                          'r.profile.edit.credit_check_last_name.label',
                          'Legal last name'
                        )}
                        required
                      />
                    </Group>
                  </Row>
                  <Row>
                    <Group col={colSize}>
                      <FieldDatePicker
                        name="dob"
                        initialFocusedDate={initialMonth()}
                        placeholder={t(
                          'r.profile.edit.credit_check_date_of_birth.label',
                          'Date of birth'
                        )}
                        required
                        minDate={moment().subtract(100, 'years')}
                        maxDate={moment().subtract(13, 'years')}
                        displayFormat="YYYY-MM-DD"
                        disableFuture
                      />
                    </Group>
                  </Row>

                  <Subtitle top={30}>
                    {t(
                      'r.profile.edit.credit_check_current_address_title"',
                      'Your Current Address'
                    )}
                  </Subtitle>
                  <Row>
                    <Group col={colSize}>
                      <AddressChangeLabelWrapper>
                        <AddressChangeLabel>
                          {t(
                            'r.profile.edit.credit_check_current_address_text',
                            'Address change remaining:'
                          )}
                        </AddressChangeLabel>
                        <AddressChangeCounter>{availableAttempts}</AddressChangeCounter>
                        <Tooltip
                          text={t(
                            'r.profile.edit.credit_check_attempts_tooltip_text',
                            'For your security, we only allow a maximum of 3 address change attempts for your credit check. If you have entered your address incorrectly after 3 attempts, please contact support@liv.rent.'
                          )}
                          openOnMobilePress={true}
                          placement={displayMobile ? 'top' : 'right'}
                        >
                          <TooltipWrapper>
                            <CreditCheckToolTipIcon />
                          </TooltipWrapper>
                        </Tooltip>
                      </AddressChangeLabelWrapper>
                    </Group>
                    {!isAddressEmpty && (
                      <Group col={colSize}>
                        <AddressChangeButtonWrapper onClick={handleChangeAddress}>
                          <CreditCheckChangeAddressIcon />
                          {t(
                            'r.profile.edit.credit_check_current_address_change_text',
                            'Change Address'
                          )}
                        </AddressChangeButtonWrapper>
                      </Group>
                    )}
                  </Row>
                  {isAddressEmpty ? (
                    <>
                      <Row>
                        <Group col={colSizeFull}>
                          <AddRessSearchInputWrapper>
                            <AddressSearchInput
                              onChange={handleChange}
                              name="canada_post"
                              placeholder={t('r.profile.edit.address.text', 'Address')}
                              className={lookupInputError ? 'error' : ''}
                              required
                            />
                            {lookupInputError && (
                              <p
                                data-testid="errors-canada-post"
                                role="alert"
                                class="error__ErrorText-hmtCaz MmILz"
                              >
                                No results found for this address. Please contact support@liv.rent.
                              </p>
                            )}
                          </AddRessSearchInputWrapper>
                          {currentValues.length !== 0 && (
                            <AddressResultsDropdown>
                              <AddressResultsContainer>
                                {currentValues.map(item => {
                                  return formatText(item)
                                })}
                              </AddressResultsContainer>
                              <LogoContainer>
                                <img src={AddressCompleteLogo} alt="" />
                              </LogoContainer>
                            </AddressResultsDropdown>
                          )}
                        </Group>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Row>
                        <Group col={colSize}>
                          <FieldInput
                            name="suite"
                            placeholder={t(
                              'r.profile.edit.credit_check_suite_no.label',
                              'Suite number'
                            )}
                            value={currentAddress?.unit_number}
                            disabled={!!currentAddress}
                          />
                        </Group>
                        <Group col={colSize}>
                          <FieldInput
                            name="street_number"
                            placeholder={t(
                              'r.profile.edit.credit_check_street_no.label',
                              'Street number'
                            )}
                            required
                            value={currentAddress?.street_number}
                            disabled={!!currentAddress}
                          />
                        </Group>
                        <Group col={colSize}>
                          <FieldInput
                            name="street_name"
                            placeholder={t(
                              'r.profile.edit.credit_street_name.label',
                              'Street name'
                            )}
                            required
                            value={currentAddress?.street_name}
                            disabled={!!currentAddress}
                          />
                        </Group>
                        <Group col={colSize}>
                          <FieldInput
                            name="city"
                            placeholder={t('r.profile.edit.credit_check_city.label', 'City')}
                            required
                            value={currentAddress?.city}
                            disabled={!!currentAddress}
                          />
                        </Group>
                      </Row>
                      <Row>
                        <Group col={colSize}>
                          <FieldInput
                            placeholder={t(
                              'r.profile.edit.credit_check_province.label',
                              'Province'
                            )}
                            required
                            name="province"
                            value={currentAddress?.state_code}
                            disabled={!!currentAddress}
                          />
                        </Group>
                        <Group col={colSize}>
                          <FieldInput
                            name="zip"
                            placeholder={t(
                              'r.profile.edit.credit_check_postal_code.label',
                              'Postal code'
                            )}
                            required
                            value={currentAddress?.zip}
                            disabled={!!currentAddress}
                          />
                        </Group>
                      </Row>
                      <Row>
                        <Group col={colSize}>
                          <FieldInput
                            placeholder={t('r.profile.edit.credit_check_country.label', 'Country')}
                            value={currentAddress?.country}
                            name="country"
                            disabled
                          />
                        </Group>
                        <Group col={colSize}>
                          <UseThisAddressOptionWrapper>
                            <Tooltip
                              text={
                                toggled
                                  ? t(
                                      'r.profile.edit.credit_check_already_use_tooltip_text',
                                      'The address listed in your profile is already using this address.'
                                    )
                                  : ''
                              }
                              placement="right"
                            >
                              <FieldSwitchToggleSmallWrapper>
                                <FieldSwitchToggleSmallStyled
                                  name="hide_unit_number"
                                  id="hide_unit_number"
                                  aria-labelledby="unit-number-label"
                                  onChange={handleToogle}
                                  checked={toggled}
                                  className={toggled ? 'toggled' : ''}
                                />
                              </FieldSwitchToggleSmallWrapper>
                            </Tooltip>

                            <UseThisAddressOptionTextWrapper>
                              {t(
                                'r.profile.edit.credit_check_current_address_use_this_text',
                                'Use this address in my profile?'
                              )}
                              <Tooltip
                                text={
                                  'If selected, this address will also update the current address in your profile. '
                                }
                                placement="right"
                              >
                                <CreditCheckToolTipIcon />
                              </Tooltip>
                            </UseThisAddressOptionTextWrapper>
                          </UseThisAddressOptionWrapper>
                        </Group>
                      </Row>
                    </>
                  )}
                  <Subtitle top={30}>
                    {t('r.profile.edit.identification_documents.label', 'Identification Documents')}
                  </Subtitle>
                  <Row>
                    <Group col={colSize}>
                      <FieldInput
                        name="sin"
                        placeholder={t(
                          'r.profile.edit.credit_check_sin_no.label',
                          'Social insurance number (Optional)'
                        )}
                      />
                    </Group>
                    <Group col={colSize}>
                      <FieldInput
                        name="driver_license"
                        placeholder={t(
                          'r.profile.edit.credit_check_drivers_license_no.label',
                          `Driver's licence number (Optional)`
                        )}
                      />
                    </Group>
                  </Row>
                  <Row>
                    <Group col={colSize}>
                      <TooltipRenderer
                        id="cancelToolTip"
                        tooltip={
                          <Box>
                            <Text weight="regular" size="small">
                              {t(
                                'r.profile.credit_check_tooltip_title',
                                'This helps ensure accuracy with verifying your identity when pulling credit information from Equifax®. All user information is securely stored and is deleted after submitting.'
                              )}
                            </Text>
                          </Box>
                        }
                      >
                        {({ triggerProps }) => (
                          <Box {...triggerProps} cursor="pointer">
                            <Notice tone="info">
                              <Text weight="medium" size="standard">
                                {t(
                                  'r.profile.edit.why_do_we_need_this.label',
                                  'Why do we need this?'
                                )}
                              </Text>
                            </Notice>
                          </Box>
                        )}
                      </TooltipRenderer>
                    </Group>
                  </Row>
                  <Row top={20}>
                    <Group col={displayMobile ? 1 : 0.5}>
                      <FieldInput id="privacy" name="privacy" type="checkbox" />
                    </Group>
                    <Group col={10}>
                      <Label htmlFor="privacy">
                        <Trans i18nKey="l.payment_settings.view.terms_privacy_3.label">
                          I have read and agree to liv.rent's{' '}
                          <LinkText
                            as="a"
                            href={`${WEB_URL}/privacy`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Privacy Policy
                          </LinkText>{' '}
                          &{' '}
                          <LinkText
                            as="a"
                            href={`${WEB_URL}/terms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms & Conditions
                          </LinkText>
                          .
                        </Trans>
                      </Label>
                    </Group>
                  </Row>
                  <Row top={10}>
                    <Group col={12}>
                      <Label>
                        {t(
                          'r.profile.edit.credit_check_fine_print',
                          'By clicking verify, I authorize the disclosure of this information by Liv to certain authorized consumer reporting agencies in order to request my credit history information to prepare my Trust Score.'
                        )}
                      </Label>
                    </Group>
                  </Row>
                </PanelContainer>
              </ContentWrapperStyled>
            </Page>
            <DefaultButtonBar handleBack={() => handleActionTypeHandler('cancel')}>
              <Button
                theme="primary"
                size="small"
                type="button"
                disabled={!values.privacy}
                isSubmitting={isSubmitting}
                onClick={() => handleActionTypeHandler('verify')}
              >
                {t('r.profile.edit.credit_check_verify.button', 'Verify')}
              </Button>
            </DefaultButtonBar>
          </Form>
        )}
      </Formik>
      <CreditCheckModal
        width="xsmall"
        visible={visible}
        onClose={handleModalClose}
        actionType={actionType}
        setActionType={setActionType}
        handleBackPropFunction={handleBackPropFunction}
        submitCreditCheck={submitCreditCheck}
      />
    </>
  )
}

export default withRouter(memo(CreditCheck))
